import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import Iconify from '../../theme/iconify/iconify';

import DocumentModal from './DocumentModal';
import DocumentCard from '../doc/DocumentCard';
import Notification from '../util/Notification';
import ConfirmationModal from '../util/ConfirmationModal';
import {mislecAPI} from "../../util/axios";

function Corpus({ user, updateUser }) {
    //  Corpus _id
    const params = useParams();
    const _id = params.id;

    const [corpus, setCorpus] = React.useState(null);
    const [title, setTitle] = React.useState('');
    const [editing, setEditing] = React.useState(false);
    const [documents, setDocuments] = React.useState([]);
    const [fetched, setFetched] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);
    const [removeDocumentModal, setRemoveDocumentModal] = React.useState(false);
    const [removeDocumentId, setRemoveDocumentId] = React.useState(null);
    const [documentModal, setDocumentModal] = React.useState(false);
    const [removeCorpus, setRemoveCorpus] = React.useState(false);

    const { t } = useTranslation();
    const navigate = useNavigate();

    const fetchCorpus = async (initialLoad) => {
        if (!user) return;

        //  Fetch corpus from the server
        mislecAPI.get(`/api/corpus/${_id}`)
            .then(async (res) => {
                setCorpus(res.data.corpus);
                setDocuments(res.data.documents);
                if (initialLoad) setFetched(true);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                if (initialLoad) setFetched(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const updateTitle = () => {
        if (!title) return;

        //  Request body
        let data = { ...corpus };
        data.title = title;

        //  Update title on the server
        mislecAPI.put(`/api/corpus/${_id}`, data)
            .then(res => {
                //  Notify user of successful update
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                setEditing(false);
                fetchCorpus();
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.status === 401)
                        updateUser(null);
            });
    }

    const updateDocuments = (selectedDocuments) => {
        if (!selectedDocuments) return;

        //  Request body
        let data = { ...corpus };
        data.documents = [...corpus.documents, ...selectedDocuments];

        //  Update documents on the server
        mislecAPI.put(`/api/corpus/${_id}`, data)
            .then(res => {
                //  Notify user of successful update
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                setDocumentModal(false);
                fetchCorpus();
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const embedDocuments = () => {
        //  Request body
        const data = corpus.documents;

        //  Embed documents
        mislecAPI.post(`/api/document/embed`, data)
            .then(res => {
                //  Notify user of successful update
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                fetchCorpus();
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const removeDocument = () => {
        if (!removeDocumentId) return;

        //  Request body
        let data = { ...corpus };
        data.documents = data.documents.filter(id => id !== removeDocumentId);

        //  Remove document from the server
        mislecAPI.put(`/api/corpus/${_id}`, data)
            .then(res => {
                //  Notify user of successful removal
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                setRemoveDocumentId(null);
                fetchCorpus();
                setRemoveDocumentModal(false);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const remove = () => {
        //  Remove corpus from the server
        mislecAPI.delete(`/api/corpus/${_id}`)
            .then(res => {
                //  Notify user of successful removal
                setMessage(res.data.detail);
                setSeverity('success');
                setOpen(true);

                //  Close modal
                setRemoveCorpus(false);

                //  Redirect to corpus page
                setTimeout(() => {
                    navigate('/corpus');
                }, 2500);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const handleRemoveDocument = (id) => {
        setRemoveDocumentModal(true);
        setRemoveDocumentId(id);
    }

    const closeRemoveDocumentModal = () => {
        setRemoveDocumentModal(false);
        setRemoveDocumentId(null);
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            updateTitle();
        }
    }

    React.useEffect(() => {
        fetchCorpus();
    }, [user]);

    if (!fetched) {
        fetchCorpus(true);
    }

    return (
        <>
            <Container>
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
                    <Typography variant='h4'>{t('corpus.title')}</Typography>
                </Stack>

                {corpus && (
                    <>
                        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
                            <Grid container alignItems='center'>
                                <Grid item xs={editing ? 11 : 12} md={editing ? 6 : 12} sx={{ display: 'flex !important', alignItems: 'center' }}>
                                    <img src='/assets/icons/file-storage.png' alt={corpus.title} style={{ height: '2rem', marginRight: '8px' }} />
                                    {editing ?
                                        <input
                                            id='mISLec_edit_corpus_title'
                                            type='text'
                                            className='mISLec_edit_corpus_title'
                                            name='title'
                                            value={title}
                                            onClick={(e) => e.preventDefault()}
                                            onChange={(e) => setTitle(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            style={{
                                                color: '#637381 !important'
                                            }}
                                        />
                                        :
                                        <Typography className='mISLec_document_title' variant='body1'>{corpus.title}</Typography>
                                    }
                                </Grid>
                            </Grid>
                            {editing ?
                                <Grid item xs={editing ? 1 : 12} md={editing ? 6 : 12}>
                                    <Grid container justifyContent='flex-end'>
                                        <Grid item xs={6}>
                                            <Tooltip title={t('corpus.tooltips.save')} arrow>
                                                <IconButton
                                                    disableRipple
                                                    disableFocusRipple
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        updateTitle();
                                                    }}
                                                    sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0 }}>
                                                    <Iconify icon='eva:checkmark-outline' />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Tooltip title={t('corpus.tooltips.cancel')} arrow>
                                                <IconButton
                                                    disableRipple
                                                    disableFocusRipple
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        setEditing(false);
                                                        setTitle('');
                                                    }}
                                                    sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0, color: 'error.main' }}>
                                                    <Iconify icon='eva:close-outline' />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                :
                                <>
                                    <Tooltip title={t('corpus.tooltips.rename')} arrow>
                                        <IconButton
                                            onClick={() => {
                                                setTitle(corpus.title);
                                                setEditing(true);
                                                setTimeout(() => {
                                                    document.getElementById('mISLec_edit_corpus_title').focus();
                                                }, 100);
                                            }}
                                        >
                                            <Iconify icon='eva:edit-outline' />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('corpus.tooltips.add')} arrow>
                                        <IconButton
                                            onClick={() => setDocumentModal(true)}
                                        >
                                            <Iconify icon='fluent:document-add-24-regular' />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('corpus.tooltips.embed')} arrow>
                                        <IconButton
                                            onClick={() => embedDocuments()}
                                        >
                                            <Iconify icon='icomoon-free:embed2' />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('corpus.tooltips.remove')} arrow>
                                        <IconButton
                                            color='error'
                                            onClick={() => setRemoveCorpus(true)}
                                        >
                                            <Iconify icon='eva:trash-outline' />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            }
                        </Stack>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant='h6'>{t('corpus.documents')}</Typography>
                            </Grid>
                            {documents.length > 0 ?
                                documents.map(document => (
                                    <DocumentCard key={document._id} document={document} removeDocument={handleRemoveDocument} inCorpus />
                                ))
                                :
                                <Grid item xs={12} variant='body2'>
                                    <Typography>{t('corpus.empty.start')} <Link onClick={() => setDocumentModal(true)}>{t('corpus.empty.links.add')}</Link> {t('corpus.empty.middle')} <b>{t('corpus.empty.mISLec')}</b>{t('corpus.empty.suffix')}.</Typography>
                                </Grid>
                            }
                        </Grid>
                    </>
                )}
                {!corpus && (
                    fetched ?
                        <Grid item xs={12} variant='body2'>
                            <Typography>{t('corpus.noCorpus')}</Typography>
                        </Grid>
                        :
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '700px' }}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                )}
            </Container>
            <ConfirmationModal
                type='remove'
                title={t('corpus.modals.removeDocument.title')}
                description={t('corpus.modals.removeDocument.description')}
                confirmationText={t('corpus.modals.removeDocument.confirmation')}
                confirm={removeDocument}
                open={removeDocumentModal}
                onClose={closeRemoveDocumentModal}
            />
            <ConfirmationModal
                type='remove'
                title={t('corpus.modals.removeCorpus.title')}
                description={t('corpus.modals.removeCorpus.description')}
                confirmationText={t('corpus.modals.removeCorpus.confirmation')}
                confirm={remove}
                open={removeCorpus}
                onClose={() => setRemoveCorpus(false)}
            />
            <DocumentModal user={user} updateUser={updateUser} corpus={corpus} updateDocuments={updateDocuments} documentModal={documentModal} setDocumentModal={setDocumentModal} open={open} setOpen={setOpen} setMessage={setMessage} setSeverity={setSeverity} />
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default Corpus;